import { useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { FOCUS_AREAS } from "../data/content";
import { Icon, Overline } from "./Bits";
import { Reveal } from "./Reveal";

export function FocusAreas() {
  const [active, setActive] = useState(0);
  const area = FOCUS_AREAS[active];
  const imageRef = useRef(null);

  const select = (i) => {
    setActive(i);
    if (typeof window !== "undefined" && window.matchMedia("(max-width: 1023px)").matches) {
      requestAnimationFrame(() => {
        const el = imageRef.current;
        if (!el) return;
        const y = el.getBoundingClientRect().top + window.scrollY - 88;
        window.scrollTo({ top: y, behavior: "smooth" });
      });
    }
  };

  return (
    <section className="relative mx-auto max-w-7xl px-5 py-24 md:px-8 md:py-32" data-testid="focus-areas">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <Reveal><Overline>Our Focus Areas</Overline></Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-5 max-w-xl font-heading text-4xl font-bold tracking-tight sm:text-5xl">
              Five pillars, one <span className="italic text-gold">mission</span>.
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.16}>
          <p className="max-w-sm text-sm leading-relaxed text-muted-foreground">
            Through scholarships, grants, programs, and community partnerships, we create lasting
            impact across the areas that matter most.
          </p>
        </Reveal>
      </div>

      <div className="mt-14 grid gap-10 lg:grid-cols-[1.05fr_0.95fr]">
        <div className="order-2 lg:order-1">
          {FOCUS_AREAS.map((a, i) => (
            <button
              key={a.id}
              onMouseEnter={() => setActive(i)}
              onFocus={() => setActive(i)}
              onClick={() => select(i)}
              data-testid={`focus-item-${a.id}`}
              className={`group flex w-full items-start gap-5 border-b border-border py-6 text-left transition-colors ${
                active === i ? "opacity-100" : "opacity-60 hover:opacity-100"
              }`}
            >
              <span
                className={`mt-1 font-heading text-sm font-semibold tabular-nums transition-colors ${
                  active === i ? "text-gold" : "text-muted-foreground"
                }`}
              >
                {a.number}
              </span>
              <span
                className={`grid h-11 w-11 shrink-0 place-items-center rounded-full border transition-colors ${
                  active === i ? "border-gold bg-gold/10 text-gold" : "border-border text-foreground"
                }`}
              >
                <Icon name={a.icon} className="h-5 w-5" />
              </span>
              <span className="flex-1">
                <span className="flex items-center gap-2 font-heading text-xl font-semibold tracking-tight md:text-2xl">
                  {a.title}
                  <ArrowUpRight
                    className={`h-4 w-4 transition-all ${
                      active === i ? "translate-x-0.5 -translate-y-0.5 text-gold opacity-100" : "opacity-0"
                    }`}
                  />
                </span>
                <span className="mt-1.5 block max-w-md text-sm leading-relaxed text-muted-foreground">
                  {a.blurb}
                </span>
              </span>
            </button>
          ))}
        </div>

        <div className="order-1 lg:order-2 lg:sticky lg:top-28 lg:h-fit">
          <div ref={imageRef} className="relative aspect-[4/5] overflow-hidden rounded-2xl border border-border">
            <div className="absolute inset-0 z-10 ring-1 ring-inset ring-gold/30" />
            <AnimatePresence mode="wait">
              <motion.img
                key={area.id}
                src={area.image}
                alt={area.title}
                initial={{ opacity: 0, scale: 1.06 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className="h-full w-full object-cover"
              />
            </AnimatePresence>
            <div className="absolute inset-x-0 bottom-0 z-20 bg-gradient-to-t from-navy-deep via-navy-deep/85 to-transparent px-7 pb-7 pt-24">
              <AnimatePresence mode="wait">
                <motion.p
                  key={area.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                  className="max-w-md text-sm font-medium leading-relaxed text-white drop-shadow-[0_1px_6px_rgba(9,18,43,0.8)]"
                >
                  {area.detail}
                </motion.p>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
